import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  getDoc,
  getDocs,
  writeBatch,
  arrayUnion,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type { Comment, FeedbackItem, FeedbackStatus, UserInfo } from '../types';
import {
  deleteStoredFile,
  isStorageUrl,
  uploadImageDataUrl,
  uploadScreenRecording,
} from './storage';
import { log } from '../utils/logger';

const COLLECTION_NAME = 'feedback';

/**
 * Fields a client never writes through `updateFeedback`: identity and
 * ownership are fixed at creation, and archiving has its own operation.
 *
 * Editing a ticket used to send the whole item back, which overwrote the
 * server `createdAt` Timestamp with an ISO *string*. Firestore orders strings
 * after timestamps, so edited tickets fell out of date order and out of reach
 * of `archiveFeedbackBefore`'s range query.
 */
const PROTECTED_FIELDS = [
  'id',
  'createdAt',
  'updatedAt',
  'projectId',
  'userId',
  'createdBy',
  'archived',
  'archivedAt',
  // Comments only ever change through addFeedbackComment's atomic append;
  // an edit writing the array back would drop comments posted meanwhile.
  'comments',
] as const;

/**
 * Prepare a value for Firestore: drop `undefined` (which Firestore rejects),
 * keep everything else as-is — including `null`, which is how a field is
 * cleared (unassigning, removing a due date), and empty arrays.
 *
 * The previous version also dropped nulls and empty objects, so nothing could
 * ever be cleared, and an update whose fields were all empty became
 * `updateDoc(ref, undefined)` and threw.
 */
export const toFirestoreValue = (value: any): any => {
  if (Array.isArray(value)) {
    return value.filter((v) => v !== undefined).map(toFirestoreValue);
  }
  if (value && typeof value === 'object' && value.constructor === Object) {
    const out: Record<string, any> = {};
    for (const [key, v] of Object.entries(value)) {
      if (v !== undefined) out[key] = toFirestoreValue(v);
    }
    return out;
  }
  return value;
};

const toIso = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  return undefined;
};

/**
 * Firestore document → FeedbackItem.
 *
 * Spreads the stored data rather than picking fields one by one: the picked
 * list silently dropped anything it didn't name (`originalImageSrc`,
 * `replacementImageUrl`, `assignedTo`…), so image-change tickets lost their
 * before/after on every reload. Only fields that need normalising are
 * touched.
 */
export const toFeedbackItem = (id: string, data: Record<string, any>): FeedbackItem => {
  const { projectId: _p, userId, ...rest } = data;
  const createdAt = toIso(data.createdAt) ?? new Date().toISOString();
  return {
    ...(rest as Partial<FeedbackItem>),
    id,
    title: data.title ?? '',
    status: data.status ?? 'open',
    priority: data.priority ?? 'medium',
    type: data.type ?? 'general',
    comments: data.comments ?? [],
    annotations: data.annotations ?? [],
    tags: data.tags ?? [],
    pageMetadata: data.pageMetadata ?? {
      url: '',
      title: '',
      screenWidth: 0,
      screenHeight: 0,
      devicePixelRatio: 1,
      userAgent: '',
      timestamp: createdAt,
      deviceType: 'desktop',
    },
    createdAt,
    updatedAt: toIso(data.updatedAt) ?? createdAt,
    createdBy: data.createdBy ?? { id: userId, name: 'User', email: '', role: 'default' },
    archived: data.archived === true,
  } as FeedbackItem;
};

/** A fresh document id, so files can be uploaded under it before the save. */
export const newFeedbackId = (): string =>
  doc(collection(getFirebaseDb(), COLLECTION_NAME)).id;

/**
 * Move any inline files (data URLs, a fresh recording) into Storage and return
 * the item pointing at their URLs. Already-stored URLs pass through untouched,
 * so this is safe to run on edits too.
 */
export const storeFeedbackAssets = async (
  feedback: FeedbackItem,
  projectId: string,
  recording?: Blob | null,
): Promise<FeedbackItem> => {
  const next = { ...feedback };
  if (next.screenshot?.startsWith('data:')) {
    next.screenshot = await uploadImageDataUrl(next.screenshot, projectId, feedback.id, 'screenshot');
  }
  if (next.designMockup?.startsWith('data:')) {
    next.designMockup = await uploadImageDataUrl(next.designMockup, projectId, feedback.id, 'mockup');
  }
  if (recording) {
    next.screenRecording = await uploadScreenRecording(recording, projectId, feedback.id);
  } else if (next.screenRecording?.startsWith('blob:')) {
    // A local preview URL means nowhere; never store it.
    next.screenRecording = undefined;
  }
  return next;
};

/**
 * Create a ticket under the id it already carries (see `newFeedbackId`).
 * Throws on failure — the caller keeps the draft and tells the user.
 */
export const createFeedback = async (
  feedback: FeedbackItem,
  projectId: string,
  userId: string,
): Promise<string> => {
  const db = getFirebaseDb();
  const { id, createdAt: _c, updatedAt: _u, ...fields } = feedback;
  const ref = doc(db, COLLECTION_NAME, id);
  await setDoc(ref, {
    ...toFirestoreValue(fields),
    projectId,
    userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  log('Feedback saved', id);
  return id;
};

/** Update editable fields. Throws on failure so the UI can roll back. */
export const updateFeedback = async (
  feedbackId: string,
  updates: Partial<FeedbackItem>,
): Promise<void> => {
  const clean: Record<string, any> = { ...updates };
  for (const field of PROTECTED_FIELDS) delete clean[field];
  await updateDoc(doc(getFirebaseDb(), COLLECTION_NAME, feedbackId), {
    ...toFirestoreValue(clean),
    updatedAt: serverTimestamp(),
  });
};

export const updateFeedbackStatus = (feedbackId: string, status: FeedbackStatus) =>
  updateFeedback(feedbackId, { status });

export const assignFeedback = (feedbackId: string, assignee: UserInfo | null) =>
  updateFeedback(feedbackId, { assignedTo: assignee });

export const setFeedbackDueDate = (feedbackId: string, dueDate: string | null) =>
  updateFeedback(feedbackId, { dueDate });


/**
 * Append a comment atomically. Writing back the whole comments array (as this
 * used to) lost one of two comments posted at the same time.
 */
export const addFeedbackComment = async (
  feedbackId: string,
  comment: Comment,
): Promise<void> => {
  await updateDoc(doc(getFirebaseDb(), COLLECTION_NAME, feedbackId), {
    comments: arrayUnion(toFirestoreValue(comment)),
    updatedAt: serverTimestamp(),
  });
};

/** Delete a ticket and every file it owns. Throws if the document delete fails. */
export const deleteFeedback = async (feedbackId: string): Promise<void> => {
  const ref = doc(getFirebaseDb(), COLLECTION_NAME, feedbackId);
  try {
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : null;
    const files = [data?.screenshot, data?.designMockup, data?.screenRecording].filter(isStorageUrl);
    await Promise.all(files.map(deleteStoredFile));
  } catch (_) {
    // File cleanup is best-effort; the ticket itself must still go.
  }
  await deleteDoc(ref);
};

/**
 * Archive a single ticket. The document is kept — it's just flagged so the
 * subscription stops surfacing it. Pass `false` to restore it (there is no UI
 * for this, but it keeps the operation reversible from a script or console).
 */
export const archiveFeedback = async (
  feedbackId: string,
  archived = true
): Promise<void> => {
  await updateDoc(doc(getFirebaseDb(), COLLECTION_NAME, feedbackId), {
    archived,
    archivedAt: archived ? new Date().toISOString() : null,
  });
};

const createdAtMs = (value: any): number | null => {
  const iso = toIso(value);
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Archive every non-archived ticket in a project created strictly before
 * `before` — the "close out the last phase" operation.
 *
 * Returns the number of tickets archived. Writes are batched (Firestore caps a
 * batch at 500 operations, so they're chunked) and already-archived tickets are
 * skipped so a re-run doesn't churn `archivedAt`.
 *
 * Filters by date in the client rather than with a range query: tickets edited
 * before the `createdAt` fix hold it as a string, which a Timestamp range query
 * silently skips. This is a rare, deliberate operation, so reading the
 * project's tickets once is an acceptable cost for not missing any.
 */
export const archiveFeedbackBefore = async (
  projectId: string,
  before: Date
): Promise<number> => {
  const db = getFirebaseDb();
  const snapshot = await getDocs(
    query(collection(db, COLLECTION_NAME), where('projectId', '==', projectId))
  );

  const cutoff = before.getTime();
  const targets = snapshot.docs.filter((d) => {
    const data = d.data();
    if (data.archived === true) return false;
    const ms = createdAtMs(data.createdAt);
    return ms !== null && ms < cutoff;
  });
  if (targets.length === 0) return 0;

  const archivedAt = new Date().toISOString();
  const BATCH_LIMIT = 500;
  for (let i = 0; i < targets.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const target of targets.slice(i, i + BATCH_LIMIT)) {
      batch.update(target.ref, { archived: true, archivedAt });
    }
    await batch.commit();
  }

  log(`Archived ${targets.length} ticket(s) created before ${before.toISOString()}`);
  return targets.length;
};

/**
 * One-off repair for tickets whose `createdAt` was overwritten with a string
 * by the old edit flow. Converts it back to a Timestamp so ordering works.
 * Returns how many were fixed. Safe to re-run.
 */
export const repairFeedbackDates = async (projectId: string): Promise<number> => {
  const db = getFirebaseDb();
  const snapshot = await getDocs(
    query(collection(db, COLLECTION_NAME), where('projectId', '==', projectId))
  );
  const broken = snapshot.docs.filter((d) => typeof d.data().createdAt === 'string');
  for (let i = 0; i < broken.length; i += 500) {
    const batch = writeBatch(db);
    for (const d of broken.slice(i, i + 500)) {
      const date = new Date(d.data().createdAt);
      if (!Number.isNaN(date.getTime())) {
        batch.update(d.ref, { createdAt: Timestamp.fromDate(date) });
      }
    }
    await batch.commit();
  }
  return broken.length;
};

export const subscribeFeedback = (
  projectId: string,
  callback: (feedback: FeedbackItem[]) => void,
  onError?: (error: Error) => void,
): (() => void) => {
  const q = query(
    collection(getFirebaseDb(), COLLECTION_NAME),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q,
    (snapshot) => {
      // Archived tickets are dropped here rather than in the query. Firestore
      // inequality/equality filters skip documents that lack the field, so
      // where('archived','==',false) would silently hide every ticket created
      // before archiving existed. Filtering in the client treats a missing
      // field as "not archived", which is what we want, and avoids a new
      // composite index.
      const feedbackItems = snapshot.docs
        .filter((d) => d.data().archived !== true)
        .map((d) => toFeedbackItem(d.id, d.data()));
      log('Feedback snapshot:', feedbackItems.length, 'tickets');
      callback(feedbackItems);
    },
    (error) => {
      console.error('[markup] Feedback subscription failed:', error.code);
      if (error.code === 'failed-precondition') {
        // Carries the console link for creating the missing index.
        console.error(error.message);
      }
      onError?.(error);
    }
  );
};
